import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import config from '../config';
import { ArrowLeft, ArrowRight, ShieldCheck, Loader2, AlertCircle, CheckCircle2, RefreshCw } from 'lucide-react';

export default function CodeVerification() {
    const navigate = useNavigate();
    const location = useLocation();
    const phone = location.state?.phone || "";
    const isSignup = location.state?.isSignup || false;

    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [timeLeft, setTimeLeft] = useState(108);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [error, setError] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const inputRefs = useRef([]);
    const currentYear = new Date().getFullYear();

    const maskPhone = (phoneStr) => {
        if (!phoneStr) return "";
        const cleanPhone = phoneStr.replace(/\s/g, '');
        const last4 = cleanPhone.slice(-4);
        return `+56*******${last4}`;
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    useEffect(() => {
        if (timeLeft > 0) {
            const timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
            return () => clearTimeout(timerId);
        }
    }, [timeLeft]);

    const handleChange = (index, e) => {
        const value = e.target.value;
        if (isNaN(value)) return;
        const newCode = [...code];
        newCode[index] = value.substring(value.length - 1);
        setCode(newCode);
        setError('');
        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleResend = async () => {
        setResendLoading(true);
        setError('');
        const endpoint = isSignup
            ? `${config.apiBaseUrl}/api/sucursal-virtual/client-signup`
            : `${config.apiBaseUrl}/api/sucursal-virtual/client-signin`;
        const payload = {
            eid: config.aprEid,
            phone: phone.startsWith('+56') ? phone : `+56${phone}`
        };
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok && data.status !== false) {
                setTimeLeft(108);
                setCode(['', '', '', '', '', '']);
                inputRefs.current[0].focus();
            } else {
                setError(data.message || 'No pudimos reenviar el código.');
            }
        } catch (err) {
            setError('Error de conexión.');
        } finally {
            setResendLoading(false);
        }
    };

    const handleVerify = async () => {
        const fullCode = code.join('');
        if (fullCode.length !== 6) {
            setError('Ingresa los 6 dígitos.');
            return;
        }
        setLoading(true);
        setError('');
        const endpoint = isSignup
            ? `${config.apiBaseUrl}/api/sucursal-virtual/client-signup-verification`
            : `${config.apiBaseUrl}/api/sucursal-virtual/client-signin-verification`;
        const payload = {
            eid: config.aprEid,
            phone: phone,
            code: fullCode
        };
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (response.ok) {
                if (isSignup) {
                    setShowSuccessModal(true);
                } else {
                    const expiresInMinutes = 15;
                    const expirationTime = Date.now() + (expiresInMinutes * 60 * 1000);
                    sessionStorage.setItem('token', data.token);
                    sessionStorage.setItem('user', JSON.stringify(data.user));
                    sessionStorage.setItem('session_expiry', expirationTime.toString());
                    navigate('/inicio');
                }
            } else {
                setError(data.message || 'Código inválido o expirado.');
            }
        } catch (err) {
            setError('Error de conexión.');
        } finally {
            setLoading(false);
        }
    };

    const PoweredByFooter = ({ isDark = false, className = "" }) => (
        <div className={`flex flex-col items-center gap-1 ${className}`}>
            <span className={`text-[9px] font-bold uppercase tracking-widest text-center ${isDark ? 'text-sky-300/40' : 'text-slate-400'}`}>Powered by</span>
            <img className={isDark ? "image-invert-white" : ""} alt="Snap" src="/logo.snap.png" style={{height: '22px'}} />
            <div className={`text-[9px] text-center font-bold ${isDark ? 'text-sky-300/60' : 'text-slate-400'}`}>
                © {currentYear} {config.aprName}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen w-full flex bg-slate-50 font-sans text-slate-600 relative overflow-hidden">
            {showSuccessModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"></div>
                    <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-8 text-center animate-in zoom-in-95 duration-300">
                        <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 size={48} />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-800 mb-2">¡Registro Exitoso!</h3>
                        <p className="text-slate-500 mb-8 leading-relaxed">Cuenta creada correctamente. Ya puedes ingresar.</p>
                        <button onClick={() => navigate('/login')} className="w-full py-4 bg-sky-600 text-white rounded-xl font-bold shadow-lg hover:bg-sky-700 transition-all active:scale-95">Aceptar e Ingresar</button>
                    </div>
                </div>
            )}

            <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between overflow-hidden bg-sky-600">
                <div className="absolute inset-0"><img src="/background.avif" alt="Agua" className="w-full h-full object-cover opacity-50 mix-blend-overlay" /></div>
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-800/40 to-transparent"></div>
                <div className="relative z-10 flex flex-col justify-between h-full p-16 text-white w-full">
                    <div className="flex items-center gap-3">
                        <img className="image-invert-white" style={{height:'120px'}} src={config.aprLogo} alt={config.aprName} />
                    </div>
                    <div className="space-y-6">
                        <h1 className="text-5xl font-bold leading-tight">Protegemos <span className="text-sky-300">tu información.</span></h1>
                        <p className="text-lg text-sky-100 max-w-md leading-relaxed">La verificación asegura que solo tú puedas acceder a tu cuenta.</p>
                        <div className="mt-8 p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                            <div className="flex gap-4">
                                <div className="p-3 bg-emerald-500/20 rounded-full h-fit"><ShieldCheck className="text-emerald-300" size={24} /></div>
                                <div><h3 className="font-bold text-lg mb-1">Seguridad Activa</h3><p className="text-sm text-sky-100">Validamos tu identidad enviando un código único a tu celular.</p></div>
                            </div>
                        </div>
                    </div>
                    <PoweredByFooter isDark={true} className="mt-8 pb-4" />
                </div>
            </div>

            <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-12 overflow-y-auto">
                <div className="w-full max-w-md flex flex-col min-h-full py-6">
                    <div className="flex-1">
                        <div className="lg:hidden flex justify-center mb-8"><img style={{height:'80px'}} src={config.aprLogo} alt={config.aprName} /></div>
                        <div className="text-center mb-10">
                            <h2 className="text-2xl md:text-3xl font-bold text-slate-800">{isSignup ? 'Finalizar Registro' : 'Validar Código'}</h2>
                            <p className="text-slate-500 mt-2 text-sm">Ingresa el código enviado a <span className="font-bold text-slate-800">{maskPhone(phone)}</span></p>
                        </div>

                        {error && (
                            <div className="w-full mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-xs font-bold animate-pulse">
                                <AlertCircle size={18} /> {error}
                            </div>
                        )}

                        <div className="flex gap-2 md:gap-3 mb-10 justify-center w-full">
                            {code.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    maxLength={1}
                                    value={digit}
                                    disabled={loading || resendLoading}
                                    onChange={(e) => handleChange(index, e)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    className="w-10 h-12 md:w-12 md:h-14 bg-white border-2 border-slate-200 rounded-xl text-slate-800 text-2xl font-bold text-center focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-100 transition-all shadow-sm disabled:bg-slate-50"
                                />
                            ))}
                        </div>

                        <div className="flex gap-4 w-full mb-12">
                            <button disabled={loading || resendLoading} onClick={() => navigate(-1)} className="flex-1 py-3.5 px-4 border-2 border-slate-200 rounded-xl text-slate-600 font-bold flex items-center justify-center gap-2 hover:bg-slate-50 transition-all active:scale-95 text-sm"> <ArrowLeft size={18} /> Volver</button>
                            <button disabled={loading || resendLoading} onClick={handleVerify} className="flex-1 py-3.5 px-4 bg-sky-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-sky-700 transition-all active:scale-95 text-sm"> {loading ? <Loader2 className="animate-spin" size={20} /> : <>{isSignup ? 'Registrar' : 'Verificar'} <ArrowRight size={18} /></>} </button>
                        </div>

                        <div className="relative flex flex-col items-center w-full pb-10">
                            {timeLeft > 0 ? (
                                <>
                                    <div className="relative flex items-center justify-center w-16 h-16">
                                        <svg className="absolute w-full h-full -rotate-90">
                                            <circle cx="32" cy="32" r="28" stroke="#e2e8f0" strokeWidth="4" fill="none" />
                                            <circle cx="32" cy="32" r="28" stroke="#10b981" strokeWidth="4" fill="none" strokeDasharray={176} strokeDashoffset={176 - (timeLeft / 108) * 176} strokeLinecap="round" className="transition-all duration-1000 ease-linear" />
                                        </svg>
                                        <span className="text-xs font-black text-slate-700 font-mono">{formatTime(timeLeft)}</span>
                                    </div>
                                    <p className="mt-4 text-[10px] text-slate-400 font-bold uppercase tracking-wider">Espera para reintentar</p>
                                </>
                            ) : (
                                <button onClick={handleResend} disabled={resendLoading} className="flex items-center gap-2 px-6 py-2.5 bg-white border-2 border-sky-100 text-sky-600 rounded-2xl font-bold hover:bg-sky-50 transition-all shadow-sm text-xs"> {resendLoading ? <Loader2 className="animate-spin" size={16} /> : <><RefreshCw size={16} /> Reenviar código</>} </button>
                            )}
                        </div>
                    </div>

                    <div className="lg:hidden mt-auto pt-8 pb-24">
                        <PoweredByFooter isDark={false} />
                    </div>
                </div>
            </div>
        </div>
    );
}
